import { useOrgStore } from "@/store/org";
import type { Feature } from "@/types/FeatureTypes";

// Feature slugs as constants for type safety - MUST match API camelCase format
export const FEATURE_SLUGS = {
  // Profile/Search Features
  SEARCH_PROFILES: "searchProfiles",
  TARGET_MEDIA_LISTS: "targetMediaLists",

  // Media Features
  DIGITAL_MEDIA_LICENSED: "digitalMediaLicensed",
  DIGITAL_MEDIA_UNLICENSED: "digitalMediaUnlicensed",
  PRINT_MEDIA_LICENSED: "printMediaLicensed",
  PRINT_MEDIA_UNLICENSED: "printMediaUnlicensed",
  BROADCAST_MEDIA_LICENSED: "broadcastMediaLicensed",
  BROADCAST_MEDIA_UNLICENSED: "broadcastMediaUnlicensed",
  SOCIAL_MEDIA: "socialMedia",
  PODCAST_MEDIA: "podcastMedia",

  // Email Features
  EMAIL_DIGESTS: "emailDigests",
  EMAIL_ALERTS: "emailAlerts",
  EMAIL_DOMAIN_MANAGEMENT: "emailDomainManagement",

  // Content Features
  ARTICLE_VISIBILITY: "articleVisibility",
  ARTICLE_SENTIMENT: "articleSentiment",
  WORDPLAY: "wordplay",
  AI_SUMMARIES: "aiSummaries",
  PDF_ACCESS: "pdfAccess",
  PRM_ACCESS: "prmAccess",

  // Team Features
  TEAM_MANAGEMENT: "teamManagement",

  // Deprecated Features
  PPT_REPORT_EXPORT: "pptReportExport",
  PITCH_WORKFLOW: "pitchWorkflow",
  EMAIL_CAMPAIGNS: "emailCampaigns",
  AUTHOR_INTELLIGENCE: "authorIntelligence",
  TWITTER_AMPLIFICATION: "twitterAmplification",
  VERIFIED_CONTACT_DATABASE: "verifiedContactDatabase",
} as const;

export type FeatureSlug = (typeof FEATURE_SLUGS)[keyof typeof FEATURE_SLUGS];

// Media types for access checking
export const MEDIA_TYPES = {
  DIGITAL: "digital",
  PRINT: "print",
  BROADCAST: "broadcast",
} as const;

export type MediaType = (typeof MEDIA_TYPES)[keyof typeof MEDIA_TYPES];

// Locking behavior constants (matching backend)
export const LOCKING_BEHAVIORS = {
  ALL_UNLOCKED: "all_unlocked",
  ALL_LOCKED: "all_locked",
  LICENSED_ONLY_LOCKED: "licensed_only_locked",
} as const;

export type LockingBehavior =
  (typeof LOCKING_BEHAVIORS)[keyof typeof LOCKING_BEHAVIORS];

// Deprecated features tracking
const DEPRECATED_FEATURES = new Set<string>([
  FEATURE_SLUGS.PPT_REPORT_EXPORT,
  FEATURE_SLUGS.PITCH_WORKFLOW,
  FEATURE_SLUGS.EMAIL_CAMPAIGNS,
  FEATURE_SLUGS.AUTHOR_INTELLIGENCE,
  FEATURE_SLUGS.TWITTER_AMPLIFICATION,
  FEATURE_SLUGS.VERIFIED_CONTACT_DATABASE,
]);

// Media usage data types based on API response
export interface MediaUsageData {
  overall_limit: number | null;
  overall_usage: number;
  automated_usage: number;
  last_reset_date: string;
}

export interface MediaUsageResponse {
  digital_media_licensed: MediaUsageData;
  digital_media_unlicensed: MediaUsageData;
  print_media_licensed: MediaUsageData;
  print_media_unlicensed: MediaUsageData;
  broadcast_media_licensed: MediaUsageData;
  broadcast_media_unlicensed: MediaUsageData;
  pdf_access: MediaUsageData;
}

export const useFeatureManagement = () => {
  const orgStore = useOrgStore();

  /**
   * Check if a feature is enabled using dot notation
   * @param featurePath - e.g. 'search-profiles' or 'digital-media-licensed'
   */
  const isFeatureEnabled = (featurePath: string): boolean => {
    const featureSlug = featurePath.split(".")[0];

    // Log deprecation warning if applicable
    if (
      DEPRECATED_FEATURES.has(featureSlug) &&
      process.env.NODE_ENV === "development"
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `[FMS] Feature "${featureSlug}" is deprecated and should not be used.`,
      );
    }

    const features = orgStore.org?.attributes?.features;
    // No features array means feature is not enabled
    if (!Array.isArray(features)) {
      return false;
    }

    // Handle special case for "Unlicensed" feature logic
    if (featureSlug.includes("Unlicensed")) {
      const articleVisibilityFeature = features.find(
        (f: Feature) => f.slug === "articleVisibility",
      );

      // If hideUnlicensed is true, treat unlicensed features as disabled
      if (articleVisibilityFeature?.configuration?.hideUnlicensed === true) {
        return false;
      }
    }

    const feature = features.find((f: Feature) => f.slug === featureSlug);
    return !!feature;
  };

  /**
   * Get a feature configuration value using dot notation
   * @param featurePath - e.g. 'article-visibility.hide_unverified'
   */
  const getFeatureConfig = <T = any>(featurePath: string): T | null => {
    const [featureSlug, configKey] = featurePath.split(".");

    if (!configKey) {
      // eslint-disable-next-line no-console
      console.warn(
        `[FMS] Invalid feature path "${featurePath}". Expected format: "feature-slug.config-key"`,
      );
      return null;
    }

    // Log deprecation warning if applicable
    if (
      DEPRECATED_FEATURES.has(featureSlug) &&
      process.env.NODE_ENV === "development"
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `[FMS] Feature "${featureSlug}" is deprecated and should not be used.`,
      );
    }

    const features = orgStore.org?.attributes?.features;
    if (features && Array.isArray(features)) {
      const feature = features.find((f: Feature) => f.slug === featureSlug);
      if (feature?.configuration && configKey in feature.configuration) {
        return feature.configuration[configKey] as T;
      }
    }

    // No configuration found
    return null;
  };

  /**
   * Check if user can access specific media type
   */
  const canAccessMedia = (
    mediaType: MediaType,
    region?: string,
    license?: string,
  ): boolean => {
    const licensedSlug = `${mediaType}MediaLicensed`;
    const unlicensedSlug = `${mediaType}MediaUnlicensed`;

    if (isFeatureEnabled(licensedSlug)) {
      const geoLocations =
        getFeatureConfig<string[]>(`${licensedSlug}.geographicLocations`) || [];
      if (region && geoLocations.length > 0) {
        if (!geoLocations.includes(region)) {
          return false;
        }
      }

      const licenses =
        getFeatureConfig<string[]>(`${licensedSlug}.${mediaType}Licenses`) ||
        [];
      if (license && licenses.length > 0) {
        if (!licenses.includes(license)) {
          return false;
        }
      }

      return true;
    }

    if (isFeatureEnabled(unlicensedSlug)) {
      const geoLocations =
        getFeatureConfig<string[]>(`${unlicensedSlug}.geographicLocations`) ||
        [];
      if (region && geoLocations.length > 0) {
        return geoLocations.includes(region);
      }
      return true;
    }

    return false;
  };

  /**
   * Get locking behavior for a specific media type (simplified for backward compatibility)
   * @param mediaType - The media type (digital, print, broadcast)
   * @returns LockingBehavior enum value
   */
  const getLockingBehavior = (mediaType: MediaType): LockingBehavior => {
    const licensedSlug = `${mediaType}MediaLicensed`;
    const unlicensedSlug = `${mediaType}MediaUnlicensed`;

    const licensedLockingEnabled = getFeatureConfig<boolean>(
      `${licensedSlug}.lockingEnabled`,
    );
    const unlicensedLockingEnabled = getFeatureConfig<boolean>(
      `${unlicensedSlug}.lockingEnabled`,
    );

    if (licensedLockingEnabled && unlicensedLockingEnabled) {
      return LOCKING_BEHAVIORS.ALL_LOCKED;
    }

    if (licensedLockingEnabled && !unlicensedLockingEnabled) {
      return LOCKING_BEHAVIORS.LICENSED_ONLY_LOCKED;
    }

    return LOCKING_BEHAVIORS.ALL_UNLOCKED;
  };

  /**
   * Determine if content should be locked based on lockingEnabled flags
   * @param mediaType - The media type
   * @param hasLicenses - Whether the content has licenses
   * @param isUnlocked - Whether the content has been unlocked (from ArticleOpenTracker)
   * @returns boolean indicating if content should appear locked
   */
  const shouldContentBeLocked = (
    mediaType: MediaType,
    hasLicenses: boolean,
    isUnlocked: boolean = false,
  ): boolean => {
    const featureSlug = hasLicenses
      ? `${mediaType}MediaLicensed`
      : `${mediaType}MediaUnlicensed`;

    const lockingEnabled = getFeatureConfig<boolean>(
      `${featureSlug}.lockingEnabled`,
    );

    if (!lockingEnabled) {
      return false;
    }

    return !isUnlocked;
  };

  /**
   * Log deprecated feature usage
   */
  const logDeprecatedUsage = (feature: string, context?: string): void => {
    if (process.env.NODE_ENV === "development") {
      const message = context
        ? `[FMS] Deprecated feature "${feature}" used in ${context}`
        : `[FMS] Deprecated feature "${feature}" used`;
      // eslint-disable-next-line no-console
      console.warn(message);
    }
  };

  /**
   * Get digital licenses from FMS features
   */
  const getDigitalLicenses = (): string[] => {
    return (
      getFeatureConfig<string[]>("digitalMediaLicensed.digitalLicenses") || []
    );
  };

  /**
   * Get print licenses from FMS features
   */
  const getPrintLicenses = (): string[] => {
    return getFeatureConfig<string[]>("printMediaLicensed.printLicenses") || [];
  };

  /**
   * Get broadcast licenses from FMS features
   */
  const getBroadcastLicenses = (): string[] => {
    return (
      getFeatureConfig<string[]>("broadcastMediaLicensed.broadcastLicenses") ||
      []
    );
  };

  /**
   * Get allowed countries/regions for a media type
   */
  const getAllowedRegions = (
    mediaType: MediaType,
    isLicensed: boolean = true,
  ): string[] => {
    const suffix = isLicensed ? "licensed" : "unlicensed";
    const featureSlug = `${mediaType}Media${suffix.charAt(0).toUpperCase() + suffix.slice(1)}`;
    return (
      getFeatureConfig<string[]>(`${featureSlug}.geographicLocations`) || []
    );
  };

  /**
   * Get license region/country from FMS
   */
  const getLicenseRegion = (): string | null => {
    // Check all media types for geographic restrictions
    const regions = [
      ...getAllowedRegions(MEDIA_TYPES.DIGITAL),
      ...getAllowedRegions(MEDIA_TYPES.PRINT),
      ...getAllowedRegions(MEDIA_TYPES.BROADCAST),
    ];

    // Return the first region found, or null
    return regions.length > 0 ? regions[0] : null;
  };

  /**
   * Get aggregated allowed countries from all enabled media features
   * Option A: Union approach - aggregate all geographicLocations across all media types
   *
   * Logic:
   * - If any feature has [] or ["GLB"] → return [] (global access)
   * - Otherwise → return union of all specified countries
   * - No legacy fallbacks - pure FMS implementation
   */
  const getAllowedCountries = (): string[] => {
    const allGeographicLocations: string[][] = [];

    // Collect geographicLocations from all enabled media features
    const mediaFeatures = [
      "digitalMediaLicensed",
      "digitalMediaUnlicensed",
      "printMediaLicensed",
      "printMediaUnlicensed",
      "broadcastMediaLicensed",
      "broadcastMediaUnlicensed",
    ];

    for (const featureSlug of mediaFeatures) {
      if (isFeatureEnabled(featureSlug)) {
        const geoLocations =
          getFeatureConfig<string[]>(`${featureSlug}.geographicLocations`) ||
          [];
        allGeographicLocations.push(geoLocations);
      }
    }

    // If no media features are enabled, return empty array (global access)
    if (allGeographicLocations.length === 0) {
      return [];
    }

    // Check if any feature has global access (empty array or ["GLB"])
    const hasGlobalAccess = allGeographicLocations.some(
      (geoLocations) =>
        geoLocations.length === 0 || geoLocations.includes("GLB"),
    );

    if (hasGlobalAccess) {
      return []; // Global access - return empty array
    }

    // Return union of all specified countries
    const allCountries = new Set<string>();
    allGeographicLocations.forEach((geoLocations) => {
      geoLocations.forEach((country) => allCountries.add(country));
    });

    return Array.from(allCountries);
  };

  /**
   * Check if user can unlock content based on current usage and FMS limits
   * Handles share_licensed_limits configuration for unlicensed content
   */
  const canUnlockContent = (
    mediaType: MediaType,
    isLicensed: boolean,
    usageData: MediaUsageResponse,
  ): {
    canUnlock: boolean;
    reason?:
      | "monthly_limit_exceeded"
      | "daily_limit_exceeded"
      | "feature_disabled";
  } => {
    // Check if feature is enabled first
    const suffix = isLicensed ? "Licensed" : "Unlicensed";
    const featureSlug = `${mediaType}Media${suffix}`;

    if (!isFeatureEnabled(featureSlug)) {
      return { canUnlock: false, reason: "feature_disabled" };
    }

    // For unlicensed content, check if it shares licensed limits
    let effectiveFeatureSlug = featureSlug;
    const effectiveUsageKey =
      `${mediaType}_media_${isLicensed ? "licensed" : "unlicensed"}` as keyof MediaUsageResponse;

    if (!isLicensed) {
      const shareLicensedLimits = getFeatureConfig<boolean>(
        `${featureSlug}.shareLicensedLimits`,
      );

      if (shareLicensedLimits) {
        // Use licensed feature's limits when sharing is enabled
        effectiveFeatureSlug = `${mediaType}MediaLicensed`;
        // Usage data will be the same for both when sharing is enabled
        // but we still use the unlicensed key as it contains the combined count
      }
    }

    // Get usage for this specific media type
    const usage = usageData[effectiveUsageKey];

    if (!usage) {
      return { canUnlock: true }; // No usage data means no restrictions
    }

    // Check limits from FMS
    const overallLimit =
      usage.overall_limit ||
      getFeatureConfig<number>(`${effectiveFeatureSlug}.overallLimit`);

    // Check monthly limit (-1 or null means unlimited)
    if (
      overallLimit !== null &&
      overallLimit !== -1 &&
      usage.overall_usage >= overallLimit
    ) {
      return { canUnlock: false, reason: "monthly_limit_exceeded" };
    }

    return { canUnlock: true };
  };

  /**
   * Check if unlicensed content shares licensed limits for a media type
   */
  const isShareLicensedLimitsEnabled = (mediaType: MediaType): boolean => {
    const unlicensedFeatureSlug = `${mediaType}MediaUnlicensed`;
    return (
      getFeatureConfig<boolean>(
        `${unlicensedFeatureSlug}.shareLicensedLimits`,
      ) === true
    );
  };

  /**
   * Get effective limits for a media type and license status
   * When share_licensed_limits is enabled, unlicensed content uses licensed limits
   */
  const getEffectiveLimits = (
    mediaType: MediaType,
    isLicensed: boolean,
  ): {
    overallLimit: number | null;
    automatedLimit: number | null;
    featureSlug: string;
  } => {
    let effectiveFeatureSlug = `${mediaType}Media${isLicensed ? "Licensed" : "Unlicensed"}`;

    // If unlicensed and sharing is enabled, use licensed limits
    if (!isLicensed && isShareLicensedLimitsEnabled(mediaType)) {
      effectiveFeatureSlug = `${mediaType}MediaLicensed`;
    }

    return {
      overallLimit: getFeatureConfig<number>(
        `${effectiveFeatureSlug}.overallLimit`,
      ),
      automatedLimit: getFeatureConfig<number>(
        `${effectiveFeatureSlug}.automatedLimit`,
      ),
      featureSlug: effectiveFeatureSlug,
    };
  };

  /**
   * Check if PDF access is available
   */
  const canAccessPdf = (
    usageData: MediaUsageResponse,
  ): {
    canAccess: boolean;
    reason?: string;
  } => {
    // Check if PDF access feature is enabled
    if (!isFeatureEnabled("pdfAccess")) {
      return { canAccess: false, reason: "feature_disabled" };
    }

    const pdfUsage = usageData.pdf_access;
    if (!pdfUsage) {
      return { canAccess: true };
    }

    const overallLimit = getFeatureConfig<number>("pdfAccess.monthlyViews");

    if (
      overallLimit !== null &&
      overallLimit !== -1 &&
      pdfUsage.overall_usage >= overallLimit
    ) {
      return { canAccess: false, reason: "monthly_limit_exceeded" };
    }

    return { canAccess: true };
  };
  return {
    // New simplified API
    isFeatureEnabled,
    getFeatureConfig,
    canAccessMedia,
    getLockingBehavior,
    shouldContentBeLocked,
    logDeprecatedUsage,
    // Media usage limit checking
    canUnlockContent,
    canAccessPdf,
    isShareLicensedLimitsEnabled,
    getEffectiveLimits,
    // License and geo helpers
    getDigitalLicenses,
    getPrintLicenses,
    getBroadcastLicenses,
    getAllowedRegions,
    getLicenseRegion,
    getAllowedCountries,
    // Constants
    FEATURE_SLUGS,
    MEDIA_TYPES,
    LOCKING_BEHAVIORS,
  };
};
